import { useState, useRef, useEffect } from 'react';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { PageHeader } from '@/components/layout/PageContainer';
import { FlagIcon } from '@/components/FlagIcon';

import { VALID_ROLES } from '../constants';

// ── MultiSelect ──────────────────────────────────────────────────────────────

interface MultiSelectProps {
  label: string;
  options: { value: string; label: React.ReactNode }[];
  selected: string[];
  onChange: (selected: string[]) => void;
  filterMode?: 'any' | 'all';
  onFilterModeChange?: (mode: 'any' | 'all') => void;
  showFilterMode?: boolean;
}

const MultiSelect = ({
  label,
  options,
  selected,
  onChange,
  filterMode,
  onFilterModeChange,
  showFilterMode,
}: MultiSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const displayText =
    selected.length === 0
      ? label
      : selected.length === 1
        ? options.find((o) => o.value === selected[0])?.label || selected[0]
        : `${selected.length} selected`;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`px-2.5 py-1 border rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-[#0014cc]/30 focus:border-[#0014cc] flex items-center gap-1.5 whitespace-nowrap shrink-0 w-[120px] transition-all hover:border-slate-400 hover:shadow-sm active:scale-[0.97] ${selected.length > 0 ? 'border-slate-950' : 'border-slate-300'}`}
      >
        <span className={`truncate ${selected.length === 0 ? 'text-slate-500' : 'text-slate-800'}`}>
          {displayText}
        </span>
        <svg
          className={`w-3.5 h-3.5 text-slate-400 ml-auto transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-52 bg-white border border-slate-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
          <div className="p-2 border-b border-slate-100 flex items-center justify-between">
            <button
              type="button"
              onClick={() => onChange([])}
              className="text-xs text-[#0014cc] hover:text-[#0010a3]"
            >
              Clear all
            </button>
            {showFilterMode && selected.length > 1 && onFilterModeChange && (
              <div className="flex items-center bg-slate-100 rounded-md p-0.5">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onFilterModeChange('any');
                  }}
                  className={`px-1.5 py-0.5 text-xs font-medium rounded transition-colors ${
                    filterMode === 'any'
                      ? 'bg-white text-[#0014cc] shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                  title="Show users with ANY selected"
                >
                  OR
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onFilterModeChange('all');
                  }}
                  className={`px-1.5 py-0.5 text-xs font-medium rounded transition-colors ${
                    filterMode === 'all'
                      ? 'bg-white text-[#0014cc] shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                  title="Show users with ALL selected"
                >
                  AND
                </button>
              </div>
            )}
          </div>
          {options.map((option) => {
            const isChecked = selected.includes(option.value);
            return (
              <label
                key={option.value}
                className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer"
                onClick={() => toggleOption(option.value)}
              >
                <div
                  className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                    isChecked ? 'bg-[#0014cc] border-[#0014cc]' : 'bg-white border-slate-300'
                  }`}
                >
                  {isChecked && (
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                </div>
                <span className="text-sm text-slate-700">{option.label}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Header ───────────────────────────────────────────────────────────────────

interface UsersHeaderProps {
  totalUsers: number;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  roleFilter: string[];
  onRoleFilterChange: (selected: string[]) => void;
  departmentFilter: string[];
  onDepartmentFilterChange: (selected: string[]) => void;
  departments: string[];
  markets: string[];
  marketFilter: string[];
  onMarketFilterChange: (selected: string[]) => void;
  marketFilterMode: 'any' | 'all';
  onMarketFilterModeChange: (mode: 'any' | 'all') => void;
  channelFilter: string[];
  onChannelFilterChange: (selected: string[]) => void;
  statusFilter: string[];
  onStatusFilterChange: (selected: string[]) => void;
  onAddUser: () => void;
}

export function UsersHeader({
  totalUsers,
  searchQuery,
  onSearchChange,
  roleFilter,
  onRoleFilterChange,
  departmentFilter,
  onDepartmentFilterChange,
  departments,
  markets,
  marketFilter,
  onMarketFilterChange,
  marketFilterMode,
  onMarketFilterModeChange,
  channelFilter,
  onChannelFilterChange,
  statusFilter,
  onStatusFilterChange,
  onAddUser,
}: UsersHeaderProps) {
  const actions = (
    <>
      {/* Search */}
      <div className="flex-1 min-w-[120px] px-4">
        <input
          type="text"
          placeholder={`Search ${totalUsers} users...`}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full px-3 py-1 border border-slate-300 rounded-lg text-sm bg-white text-slate-900 outline-none transition-all hover:border-slate-400 hover:shadow-sm focus:ring-2 focus:ring-[#0014cc]/30 focus:border-[#0014cc] focus:shadow-md"
        />
      </div>

      {/* Role Filter — driven by VALID_ROLES so a new role appears here too */}
      <MultiSelect
        label="Role"
        options={VALID_ROLES}
        selected={roleFilter}
        onChange={onRoleFilterChange}
      />

      {/* Department Filter */}
      <MultiSelect
        label="Dept."
        options={departments.map((dept) => ({ value: dept, label: dept }))}
        selected={departmentFilter}
        onChange={onDepartmentFilterChange}
      />

      {/* Market Filter */}
      <MultiSelect
        label="Market"
        options={markets.map((market) => ({
          value: market,
          label: (
            <span className="inline-flex items-center gap-1.5">
              <FlagIcon countryCode={market} className="w-4 h-auto rounded-sm" />
              {market}
            </span>
          ),
        }))}
        selected={marketFilter}
        onChange={onMarketFilterChange}
        showFilterMode={true}
        filterMode={marketFilterMode}
        onFilterModeChange={onMarketFilterModeChange}
      />

      {/* Channel Filter */}
      <MultiSelect
        label="Channel"
        options={[
          { value: 'Paid Search', label: 'Paid Search' },
          { value: 'Paid Social', label: 'Paid Social' },
        ]}
        selected={channelFilter}
        onChange={onChannelFilterChange}
      />

      {/* Status Filter */}
      <MultiSelect
        label="Status"
        options={[
          { value: 'active', label: 'Active' },
          { value: 'inactive', label: 'Inactive' },
        ]}
        selected={statusFilter}
        onChange={onStatusFilterChange}
      />

      {/* Add User */}
      <button
        onClick={onAddUser}
        className="p-1.5 bg-[#0014cc] text-white rounded-lg hover:bg-[#0010a3] transition-colors flex items-center justify-center"
        title="Add User"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      </button>
    </>
  );

  return (
    <PageHeader>
      <Breadcrumb actions={actions} />
    </PageHeader>
  );
}
